import { useEffect, useMemo, useState } from "react";
import { Filter, RotateCcw } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass, selectClass } from "../components/FormField";
import {
  fetchInsurerGrid,
  fetchInsurerHospitalGrids,
  fetchInsurerNetwork,
  type InsurerNetworkHospital,
  type TariffGrid,
} from "../api/claimsApi";

const TYPE_LABELS: Record<string, string> = {
  hopital: "Hôpital",
  clinique: "Clinique",
  pharmacie: "Pharmacie",
  laboratoire: "Laboratoire",
  cabinet_medical: "Cabinet médical",
  centre_specialise: "Centre spécialisé",
};

type NetworkFilters = { search: string; type: string; city: string };
const NO_FILTERS: NetworkFilters = { search: "", type: "", city: "" };

function distinct(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b, "fr"));
}

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function rowButton(active: boolean): string {
  return `w-full text-left transition-colors ${active ? "bg-primary/5" : "hover:bg-accent/20"}`;
}

/**
 * Compte Assurance (consultation) : réseau hospitalier, puis grilles d'un prestataire, puis actes d'une grille.
 */
export function InsurerNetworkPage() {
  const [hospitals, setHospitals] = useState<InsurerNetworkHospital[]>([]);
  const [hospitalId, setHospitalId] = useState<number | null>(null);
  const [grids, setGrids] = useState<TariffGrid[]>([]);
  const [grid, setGrid] = useState<TariffGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NetworkFilters>(NO_FILTERS);
  const [actSearch, setActSearch] = useState("");
  const [actCategory, setActCategory] = useState("");

  useEffect(() => {
    fetchInsurerNetwork()
      .then(setHospitals)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, []);

  const openHospital = async (id: number) => {
    setHospitalId(id);
    setGrid(null);
    try {
      setGrids(await fetchInsurerHospitalGrids(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    }
  };

  const openGrid = async (id: number) => {
    setActSearch("");
    setActCategory("");
    try {
      setGrid(await fetchInsurerGrid(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    }
  };

  const selectedHospital = hospitals.find((h) => h.id === hospitalId) ?? null;

  const options = useMemo(
    () => ({
      types: distinct(hospitals.map((h) => h.type)),
      cities: distinct(hospitals.map((h) => h.city)),
    }),
    [hospitals],
  );

  const visibleHospitals = useMemo(() => {
    const term = normalize(filters.search.trim());
    return hospitals.filter((h) => {
      if (term && !normalize(`${h.name} ${h.city ?? ""} ${h.address ?? ""}`).includes(term)) return false;
      if (filters.type && h.type !== filters.type) return false;
      if (filters.city && h.city !== filters.city) return false;
      return true;
    });
  }, [hospitals, filters]);
  const filtersActive = Object.values(filters).some(Boolean);

  const actCategories = useMemo(() => distinct((grid?.acts ?? []).map((a) => a.care_category_label)), [grid]);
  const visibleActs = useMemo(() => {
    const term = normalize(actSearch.trim());
    return (grid?.acts ?? []).filter((act) => {
      if (actCategory && act.care_category_label !== actCategory) return false;
      return !term || normalize(act.act_name).includes(term);
    });
  }, [grid, actSearch, actCategory]);

  const setFilter = (key: keyof NetworkFilters, value: string) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <>
      <Breadcrumbs items={[{ label: "Réseau hospitalier" }]} />
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="flex flex-col gap-6">
        <section className="theme-card p-5">
          <h1 className="mb-3 text-lg font-semibold text-foreground">
            Réseau hospitalier ({filtersActive ? `${visibleHospitals.length} / ${hospitals.length}` : hospitals.length})
          </h1>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <span className="flex items-center gap-2 self-center text-sm font-semibold text-foreground">
              <Filter size={16} className="text-primary" aria-hidden="true" />
              Filtres
            </span>
            <label className="flex min-w-[14rem] flex-[2] flex-col gap-1 text-xs text-muted">
              Prestataire
              <input
                value={filters.search}
                onChange={(event) => setFilter("search", event.target.value)}
                placeholder="Nom, ville ou adresse…"
                className={`${inputClass} h-9 text-sm`}
              />
            </label>
            <label className="flex min-w-[10rem] flex-1 flex-col gap-1 text-xs text-muted">
              Type
              <select value={filters.type} onChange={(event) => setFilter("type", event.target.value)} className={`${selectClass} h-9 text-sm`}>
                <option value="">Tous les types</option>
                {options.types.map((type) => (
                  <option key={type} value={type}>{TYPE_LABELS[type] ?? type}</option>
                ))}
              </select>
            </label>
            <label className="flex min-w-[10rem] flex-1 flex-col gap-1 text-xs text-muted">
              Ville
              <select value={filters.city} onChange={(event) => setFilter("city", event.target.value)} className={`${selectClass} h-9 text-sm`}>
                <option value="">Toutes les villes</option>
                {options.cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </label>
            {filtersActive ? (
              <button
                type="button"
                onClick={() => setFilters(NO_FILTERS)}
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-border px-3 text-sm text-foreground hover:bg-card-muted"
              >
                <RotateCcw size={14} aria-hidden="true" />
                Effacer les filtres
              </button>
            ) : null}
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Prestataire</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Ville</th>
                  <th className="px-3 py-2">Adresse</th>
                  <th className="px-3 py-2">Téléphone</th>
                  <th className="px-3 py-2">E-mail</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">N° d'agrément</th>
                </tr>
              </thead>
              <tbody>
                {visibleHospitals.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-3 text-muted">
                      {hospitals.length === 0 ? "Aucun prestataire dans le réseau." : "Aucun prestataire ne correspond aux filtres."}
                    </td>
                  </tr>
                ) : null}
                {visibleHospitals.map((hospital, index) => (
                  <tr key={hospital.id} className={`border-t border-border ${rowButton(hospital.id === hospitalId)}`}>
                    <td className="px-3 py-2 tabular-nums text-muted">{index + 1}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="font-medium text-primary hover:underline" onClick={() => void openHospital(hospital.id)}>
                        {hospital.name}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-foreground">{TYPE_LABELS[hospital.type ?? ""] ?? hospital.type ?? "—"}</td>
                    <td className="px-3 py-2 text-foreground">{hospital.city ?? "—"}</td>
                    <td className="px-3 py-2 text-foreground">{hospital.address ?? "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-foreground">{hospital.phone ?? "—"}</td>
                    <td className="px-3 py-2 text-foreground">{hospital.email ?? "—"}</td>
                    <td className="px-3 py-2 text-foreground">
                      {[hospital.contact_name, hospital.contact_phone].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-foreground">{hospital.agreement_number ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {selectedHospital ? (
          <section className="theme-card p-5">
            <h2 className="mb-3 text-lg font-semibold text-foreground">Grilles tarifaires · {selectedHospital.name}</h2>
            {grids.length === 0 ? <p className="text-sm text-muted">Aucune grille pour ce prestataire.</p> : null}
            {grids.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                    <tr>
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Code grille</th>
                      <th className="px-3 py-2">Nom grille</th>
                      <th className="px-3 py-2">Validité</th>
                      <th className="px-3 py-2 text-right">Actes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grids.map((row, index) => (
                      <tr key={row.id} className={`border-t border-border ${rowButton(row.id === grid?.id)}`}>
                        <td className="px-3 py-2 tabular-nums text-muted">{index + 1}</td>
                        <td className="px-3 py-2">
                          <button type="button" className="font-medium text-primary hover:underline" onClick={() => void openGrid(row.id)}>
                            {row.code}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-foreground">{row.name}</td>
                        <td className="px-3 py-2 text-foreground">
                          {row.valid_from} → {row.valid_to ?? "sans fin"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">{row.acts_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        ) : null}

        {grid ? (
          <section className="theme-card p-5">
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Actes de la grille {grid.code} ({grid.currency})
            </h2>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <label className="flex min-w-[14rem] flex-[2] flex-col gap-1 text-xs text-muted">
                Acte
                <input
                  value={actSearch}
                  onChange={(event) => setActSearch(event.target.value)}
                  placeholder="Nom de l'acte…"
                  className={`${inputClass} h-9 text-sm`}
                />
              </label>
              <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-muted">
                Catégorie
                <select value={actCategory} onChange={(event) => setActCategory(event.target.value)} className={`${selectClass} h-9 text-sm`}>
                  <option value="">Toutes les catégories</option>
                  {actCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
              <span className="self-center text-xs text-muted">
                {visibleActs.length} / {grid.acts?.length ?? 0} acte(s)
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Nom acte</th>
                    <th className="px-3 py-2 text-right">Prix</th>
                    <th className="px-3 py-2">Détail</th>
                    <th className="px-3 py-2">Catégorie acte</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleActs.map((act, index) => (
                    <tr key={act.act_code} className="border-t border-border">
                      <td className="px-3 py-2 tabular-nums text-muted">{index + 1}</td>
                      <td className="px-3 py-2 text-foreground">{act.act_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">{act.price.toFixed(2)}</td>
                      <td className="px-3 py-2 text-foreground">{act.unit_label ?? act.unit}</td>
                      <td className="px-3 py-2 text-foreground">{act.care_category_label ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
